// Every backend call the Purchase Order page makes, in one place.
// Functions return the axios promise unless they unwrap `data.result` (noted).
import axios from 'supports/api';
import openDocument from 'supports/helpers/openDocument';

const unwrap = (request) => request.then((res) => res.data.result);

const multipart = { headers: { 'Content-Type': 'multipart/form-data' } };

const proofsFormData = (files = [], fields = {}) => {
  const formData = new FormData();
  Array.from(files).forEach((file) => formData.append('proofs', file));
  Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
  return formData;
};

// ---- PO header -----------------------------------------------------------

/** Header + poItems + totalPrice for one PO (unwrapped). */
export const getPO = (id) => unwrap(axios.get(`/poHeader/getById/${id}`));

/** Maps the PO Data form (see detail/usePoDetail) to the backend's keys. */
const headerPayload = (form) => ({
  masterTujuanPengeluaranId: form.expenseTypeId,
  remarks: form.remarks,
  namaBankTujuan: form.targetBankName,
  namaRekeningTujuan: form.targetAccountName,
  nomorRekeningTujuan: form.targetAccountNumber,
  cabangTujuan: form.targetBranch,
  dueDate: form.dueDate,
  periodStart: form.periodStart,
  periodEnd: form.periodEnd,
  branchId: form.branchId,
});

/** Creates a PO from the form; resolves to the new header (unwrapped). */
export const createPO = (form) =>
  unwrap(axios.post('/poHeader/create', headerPayload(form)));

export const updatePO = (id, form) =>
  axios.put(`/poHeader/update/${id}`, headerPayload(form));

export const processPO = (id) => axios.post(`/poHeader/poToProcess/${id}`);
export const cancelPO = (id) => axios.post(`/poHeader/poToCancel/${id}`);
export const approvePO = (id) => axios.post(`/poHeader/poToApprove/${id}`);
export const rejectPO = (id) => axios.post(`/poHeader/poToReject/${id}`, {});

export const liquidPO = (id, { bankId, mutationDate }) =>
  axios.post(`/poHeader/poToLiquid/${id}`, {
    tanggalMutasi: mutationDate,
    idBank: bankId,
  });

export const addToGlJournal = (id, { bankId, mutationDate }) =>
  axios.post(`/gl-journal/addToGLJournal/${id}`, {
    tanggalMutasi: mutationDate,
    idBank: bankId,
  });

export const editMutationDate = (id, mutationDate) =>
  axios.put(`/poHeader/editMutationDate/${id}`, { mutationDate });

export const uploadPoProof = (id, file) => {
  const formData = new FormData();
  formData.append('proof', file);
  return axios.put(`/v2/poHeader/${id}/upload`, formData);
};

/** Opens the PO PDF in a new tab through a short-lived document link. */
export const printPO = (id) => openDocument('po', id);

// ---- PO items ------------------------------------------------------------

export const createPoItem = ({ poHeaderId, details, price, files }) =>
  axios.post(
    '/poItem/create',
    proofsFormData(files, { poHeaderId, details, price }),
    multipart,
  );

export const updatePoItem = (id, { details, price }) =>
  axios.put(`/poItem/update/${id}`, { details, price });

export const deletePoItem = (id) => axios.delete(`/poItem/delete/${id}`);

export const updatePoItemProofs = (id, files) =>
  axios.put(`/poItem/update-proofs/${id}`, proofsFormData(files));

// ---- reference data ------------------------------------------------------

/** All bank accounts (unwrapped). */
export const getBanks = () => unwrap(axios.get('/bank/all'));

/** One bank account: { id, accountName, bankName, accountNumber }. */
export const getBank = (id) =>
  unwrap(axios.get(`/bank/get-by-id/${id}`)).then((rows) => rows[0]);
